import { motion } from 'framer-motion';
import { Wrench, Shield, HeadphonesIcon } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  viewport: { once: true, margin: '-100px' },
};

const ServicesPage = () => {
  const services = [
    {
      icon: Wrench,
      title: 'Installation & Setup',
      description:
        'Expert installation and configuration of all equipment. We ensure seamless integration with your existing infrastructure.',
      image: process.env.PUBLIC_URL + '/images/product_office.webp',
    },
    {
      icon: Shield,
      title: 'Annual Maintenance Contract',
      description:
        'Comprehensive AMC packages ensuring your equipment runs flawlessly year-round. Preventive maintenance and priority support.',
      image: process.env.PUBLIC_URL + '/images/product_printer.webp',
    },
    {
      icon: HeadphonesIcon,
      title: 'Technical Support',
      description:
        'Dedicated support team available to resolve any issues. On-site assistance and remote diagnostics for rapid problem resolution.',
      image: process.env.PUBLIC_URL + '/images/product_copier.webp',
    },
  ];

  return (
    <div data-testid="services-page" className="pt-32 pb-24 bg-[#F2F0E9]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div {...fadeInUp} className="text-center mb-24">
          <h1 className="hero-title text-[#1A1A1A] mb-6 accent-magenta">
            Industrial Grade Reliability
          </h1>
          <p className="text-lg md:text-xl text-[#595959] max-w-3xl mx-auto leading-relaxed">
            Beyond equipment sales, we provide comprehensive service solutions to keep your business running smoothly.
          </p>
        </motion.div>

        {/* Services Zig-Zag Layout */}
        <div className="space-y-32">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={index}
                data-testid={`service-item-${index}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  isEven ? '' : 'lg:direction-rtl'
                }`}
              >
                <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="aspect-[4/3] overflow-hidden bg-white">
                    <img
                      loading="lazy"
                      decoding="async"
                      src={service.image}
                      alt={service.title}
                      width="800"
                      height="600"
                      className="w-full h-full object-cover img-desaturate"
                    />
                  </div>
                </div>

                <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                  <div className="mb-6">
                    <Icon size={40} className="text-[#1A1A1A]" strokeWidth={1} />
                  </div>
                  <h2 className="serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                    {service.title}
                  </h2>
                  <p className="text-lg text-[#595959] leading-relaxed">
                    {service.description}
                  </p>
                  <button
                    data-testid={`learn-more-btn-${index}`}
                    className="luxury-button mt-8"
                  >
                    Learn More
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section */}
        <motion.div
          {...fadeInUp}
          data-testid="services-cta"
          className="mt-32 text-center bg-[#EBE8E1] py-20 px-6"
        >
          <h2 className="serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
            Need a Custom Service Package?
          </h2>
          <p className="text-lg text-[#595959] mb-8 max-w-2xl mx-auto">
            We tailor our services to meet your specific business requirements.
          </p>
          <button data-testid="contact-service-btn" className="luxury-button">
            Contact Our Team
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesPage;
